import { Component, Input, OnInit } from '@angular/core';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatButton, MatIconButton } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { DepartmentResDto } from '../../_dtos/departmentResDto';
import {
  debounceTime,
  distinctUntilChanged,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import { SharedService } from '../../_services/shared.service';
import { ValidatorService } from '../../_services/validator.service';
import { RisService } from '../../_services/ris.service';
import { RisReqDto } from '../../_dtos/risReqDto';
import { RisFormData } from '../../_models/risFormData';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import {
  MatButtonToggle,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { UserResDto } from '../../_dtos/userResDto';
import { GraphService } from '../../_services/graph.service';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { MY_DATE_FORMAT } from '../../_config/dateFormatConfig';
import { DepartmentsService } from '../../_services/departments.service';
import { TranslateModule } from '@ngx-translate/core';
import { TranslatedToastService } from '../../_services/translated-toast.service';
import { RIS_DEPARTMENT_MAPPER } from './ris-department-mapper';
import { environment } from '../../../environments/environment';
import { RisProjectTypeResDto } from '../../_dtos/risProjectTypeResDto';

@Component({
  selector: 'app-order-form-ris',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatButton,
    MatError,
    CommonModule,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatIconButton,
    TranslateModule,
  ],
  templateUrl: './order-form-ris.component.html',
  styleUrl: './order-form-ris.component.css',
  standalone: true,
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
  ],
})
export class OrderFormRisComponent implements OnInit {
  @Input() isTeam!: boolean;

  private selectedPerson?: UserResDto = undefined;

  protected strategicTopics = environment.strategicTopics;

  category!: string;
  departmentShortName!: string;
  department!: DepartmentResDto;
  departments!: DepartmentResDto[];
  risProjectTypes: RisProjectTypeResDto[] = [];
  risForm!: FormGroup<RisFormData>;
  projectManagerControl = new FormControl('');
  filteredPeople?: Observable<UserResDto[]>;

  currentPage: number = 1;
  totalPages: number = 2;

  constructor(
    private risService: RisService,
    private toast: TranslatedToastService,
    private sharedService: SharedService,
    private validatorService: ValidatorService,
    private graphService: GraphService,
    private departmentsService: DepartmentsService,
  ) {}

  ngOnInit() {
    this.getDataFromSharedService();
    this.initializeForm();
    this.getRisProjectTypes();
    this.setupProjectManagerAutocomplete();
    this.setupFormListeners();
  }

  async onSubmit() {
    if (this.risForm.valid && this.isLastPage()) {
      const formValues = this.risForm.getRawValue();

      const risRequestDto: RisReqDto = {
        title: formValues.name,
        projectType: formValues.projectType!,
        leadDepartment: RIS_DEPARTMENT_MAPPER[this.departmentShortName],
        centre: formValues.strategicTopic,
        confidential: formValues.hasNonDisclosure === 'Yes',
        source: 'bernierdm',
        startDate: formValues.startDate,
        endDate: formValues.endDate,
        projectLeaderId: this.selectedPerson!.mailNickname,
        projectLeaderMail: this.selectedPerson!.userPrincipalName,
        needsTeams: this.isTeam,
      };

      this.sharedService.closeModal();
      await this.toast.show(
        'success',
        'TOASTS.RIS.SUBMIT_MESSAGE',
        'TOASTS.RIS.SUBMIT_TITLE',
      );

      this.risService.orderRis(risRequestDto).subscribe({
        next: async (risRes) => {
          if (risRes.IsSuccessful) {
            await this.toast.show(
              'success',
              'TOASTS.RIS.CREATION_SUCCESS',
              undefined,
              { projectName: risRes.bfh_projectname },
            );
          }
        },
      });
    }
  }

  get name() {
    return this.risForm.get('name');
  }

  get errorMessage() {
    if (this.name?.hasError('required')) return 'Name is required';
    else if (this.name?.hasError('hasForbiddenChars'))
      return 'Contains forbidden Character';
    else if (this.name?.hasError('exceedsMaxLength'))
      return 'The provided name is too long';
    else if (this.name?.hasError('mailNicknameUnavailable'))
      return 'This name is already taken';
    else if (this.name?.hasError('siteNameTaken'))
      return 'This site name is already taken';
    else if (this.name?.hasError('AlreadyExists'))
      return 'This projectname is already taken';
    else return '';
  }

  getRisProjectTypes() {
    this.risService.getProjectTypes().subscribe((data) => {
      this.risProjectTypes = data.sort((a, b) =>
        a.projectTypeCode.localeCompare(b.projectTypeCode),
      );
      this.risForm.controls.projectType.enable();
    });
  }

  onProjectManagerSelected(event: MatAutocompleteSelectedEvent) {
    this.selectedPerson = event.option.value;
    this.risForm.patchValue({
      projectManager: this.selectedPerson!.displayName,
    });
  }

  nextPage() {
    if (this.currentPage < this.totalPages) this.currentPage++;
  }

  previousPage() {
    if (this.currentPage > 1) this.currentPage--;
  }

  isFirstPage(): boolean {
    return this.currentPage === 1;
  }

  isLastPage(): boolean {
    return this.currentPage === this.totalPages;
  }

  private initializeForm() {
    this.risForm = new FormGroup<RisFormData>({
      name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
        asyncValidators: [this.validatorService.validateRISProjectName()],
        updateOn: 'blur',
      }),
      projectType: new FormControl(
        { value: null, disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      projectManager: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      leadingDepartment: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      startDate: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      endDate: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      hasNonDisclosure: new FormControl('No', { nonNullable: true }),
      strategicTopic: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });

    const startDateControl = this.risForm.get('startDate');
    if (startDateControl)
      this.risForm
        .get('endDate')
        ?.addValidators(
          this.validatorService.endDateValidator(startDateControl),
        );

    this.risForm.addValidators(this.validatorService.dateRangeValidator);

    this.projectManagerControl = this.risForm.get(
      'projectManager',
    ) as FormControl;
  }

  private getDataFromSharedService() {
    this.departments = this.departmentsService.getDepartments();
    this.department = this.departmentsService.getDepartment();
    const userType = this.graphService.getUserType();
    this.category = userType ? userType.toLowerCase() : 'NO CATEGORY FOUND!';
  }

  private setupFormListeners() {
    this.risForm
      .get('leadingDepartment')!
      .valueChanges.subscribe((leadDepartmentName) => {
        this.departmentsService
          .getDepartmentObservable(leadDepartmentName)
          .subscribe({
            next: (data) => {
              this.departmentShortName = data.shortName;
            },
          });
      });
  }

  private setupProjectManagerAutocomplete() {
    this.filteredPeople = this.projectManagerControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value) => {
        if (!value || value.trim() === '') return of([]);

        return this.graphService.searchPeople(value.trim());
      }),
    );
  }
}
